import { Dispatch, SetStateAction, useRef, useState } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import {
  Box,
  Divider,
  IconButton,
  List,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import { useSectionPermission } from 'hooks/usePermission'
import { isGroupMode, markMenuForGroup } from 'lib/merchant-group'
import { useRouter } from 'next/router'
import { menuItems } from 'templates/Layout/Layout'

import Can from 'components/Can'
import CanLogical from 'components/CanLogical'
import { Menu } from 'components/Drawer/components/Menu/Menu'
import InviteAndEarn from 'components/InviteAndEarn'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import MerchantAccount from '../MerchantAccount'
import { Setting } from '../MerchantAccount/MerchantAccount'
import Shortcuts from '../Shortcuts'
import * as S from './MobileDropdown.styles'

interface MobileDropdownProps {
  balance: false | number
  setIsOpenModalReport: Dispatch<SetStateAction<boolean>>
  handleOpenMenuDetails: () => void
  setIsExpandedMobileMenu: Dispatch<SetStateAction<boolean>>
  isExpandedMobileMenu: boolean
}

const MobileDropdown = ({
  balance,
  setIsOpenModalReport,
  handleOpenMenuDetails,
  setIsExpandedMobileMenu,
  isExpandedMobileMenu
}: MobileDropdownProps) => {
  const [openMenu, setOpenMenu] = useState({
    clients: false,
    receives: false
  })

  const wrapperRef = useRef<HTMLDivElement>(null)

  const routerGlobal = useRouter()
  const { signOut, user } = useAuthContext()
  const [hiddedBalance, setHiddenBalance] = useState(false)
  // Mesmo gate do menu de Configurações no desktop: Relatórios não tem mais
  // entrada no menu lateral, então quem esconder o item aqui tira o acesso.
  // Ver o comentário em `MerchantAccount`.
  const canSeeReports = useSectionPermission('reports:show')

  const handleOpenMenu = (option: string) => {
    setOpenMenu((prevState) => ({
      ...prevState,
      [option]: !openMenu[option]
    }))
  }

  const settings: Setting[] = [
    {
      action: () => {
        routerGlobal.push('/settings')
        setIsExpandedMobileMenu(false)
      },
      icon: 'settings',
      label: 'Configurações'
    }
  ]

  if (canSeeReports) {
    settings.push({
      action: () => {
        setIsOpenModalReport(true)
        setIsExpandedMobileMenu(false)
      },
      icon: 'assignment',
      label: 'Relatórios'
    })
  }

  if (
    CanLogical({
      roles: ['guest']
    })
  ) {
    const ConfigsSettingsIndex = settings.findIndex(
      ({ label }) => label === 'Configurações'
    )
    settings.splice(ConfigsSettingsIndex, 1)
  }

  return (
    <S.Wrapper ref={wrapperRef} isExpandedMobileMenu={isExpandedMobileMenu}>
      <Shortcuts setIsExpandedMobileMenu={setIsExpandedMobileMenu} />
      <S.AccountDetails>
        <MerchantAccount setIsOpenModalReport={setIsOpenModalReport} />

        {/* SC-10999: o saldo é de cada empresa; no grupo seria só o da
            empresa de contexto */}
        {!isGroupMode() && (
          <Can permission="balance:view">
            <Box display="flex" alignItems="center" width="100%" pl={2}>
              <Tooltip
                enterTouchDelay={0}
                title={Number(balance) > 0 ? 'Visualizar Detalhes' : ''}
              >
                <S.BalanceButton variant="text" onClick={handleOpenMenuDetails}>
                  <Typography variant="xsLight">Saldo</Typography>

                  {balance === false && (
                    <Skeleton
                      variant="rectangular"
                      width={64}
                      height={23}
                      sx={{ borderRadius: 2 }}
                    />
                  )}

                  {balance !== false && (
                    <Typography
                      onClick={handleOpenMenuDetails}
                      variant="smBold"
                    >
                      {hiddedBalance
                        ? 'R$ *****'
                        : formatMoney(Number(balance))}
                    </Typography>
                  )}
                </S.BalanceButton>
              </Tooltip>

              <Tooltip
                enterTouchDelay={0}
                title={hiddedBalance ? 'Mostrar saldo' : 'Esconder saldo'}
              >
                <IconButton onClick={() => setHiddenBalance(!hiddedBalance)}>
                  <Icon icon={hiddedBalance ? 'visibilityOff' : 'visibility'} />
                </IconButton>
              </Tooltip>
            </Box>
          </Can>
        )}
      </S.AccountDetails>
      <Divider sx={{ width: '100%' }} />
      <Box width="100%">
        <List sx={{ width: '100%', gap: '12px' }} component="nav">
          {/* SC-10999: no grupo o menu marca o que é por empresa, como o
              menu lateral do desktop */}
          {(isGroupMode()
            ? markMenuForGroup(menuItems[user?.role || 'guest'])
            : menuItems[user?.role || 'guest']
          ).map((item, index) => (
            <Menu
              icon={item?.icon}
              key={index}
              label={item?.label}
              handleClick={() => {
                setIsExpandedMobileMenu(false)
              }}
              router={item.router}
              isExpanded={true}
              title={item?.label}
              subheader={item?.subheader}
              openMenu={openMenu}
              handleOpenMenu={handleOpenMenu}
              option={item?.option}
              groupUnavailable={
                (item as { groupUnavailable?: boolean }).groupUnavailable
              }
            />
          ))}
          <InviteAndEarn />
        </List>
      </Box>
      <Divider sx={{ width: '100%' }} />
      <Stack alignSelf={'flex-start'} width="100%" mb={7}>
        {settings.map((setting) => {
          return (
            <S.MenuItem key={setting.label} onClick={setting.action}>
              {!!setting.icon && (
                <Icon
                  icon={setting.icon}
                  width="23px"
                  sx={{ color: theme.palette.neutral[200] }}
                />
              )}
              <Typography color={theme.palette.neutral[200]} fontWeight={600}>
                {setting.label}
              </Typography>
            </S.MenuItem>
          )
        })}
        <S.MenuItem
          onClick={async () => {
            setIsExpandedMobileMenu(false)
            await signOut()
          }}
        >
          <Icon
            icon={'exitToApp'}
            width="23px"
            sx={{ color: theme.palette.neutral[200] }}
          />

          <Typography color={theme.palette.neutral[200]} fontWeight={600}>
            Sair
          </Typography>
        </S.MenuItem>
      </Stack>
    </S.Wrapper>
  )
}

export default MobileDropdown
